import React, { useCallback, useState } from 'react';

// mui
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

// models
import {
  CareProfileCategoryID,
  FormStatus,
  combineSectionFields,
  computeSectionRows,
  Section,
} from '@karehero/models';

import { Icon } from '@/components/atoms/Icon';
import Editor from '@/components/organisms/Editor/Editor';

// types

export interface CategoryData {
  categoryID: CareProfileCategoryID | string;
  title: string;
  category?: any;
  sections?: Section[];
  forms?: any;
  answers?: any;
  setAnswers: any;
  createManyAnswer: any;
  handlePrintPdf?: () => void;
}

export interface FormEdits {
  formID?: string;
  updates?: any;
}

const sectionFormID = (sectionId: string) => `__section__:${sectionId}`;

const SectionViewRow = ({
  label,
  answer,
}: {
  label: string;
  answer: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
    }}
  >
    <Typography
      variant='body1'
      sx={{
        color: 'text.primary',
        flex: '0 0 auto',
        maxWidth: '75%',
        wordBreak: 'break-word',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='body1'
      align='right'
      sx={{
        color: 'text.secondary',
        flex: '1 1 0',
        minWidth: 0,
        pl: 4,
        wordBreak: 'break-word',
      }}
    >
      {answer}
    </Typography>
  </Box>
);

interface SectionViewProps {
  section: Section;
  forms: any;
  answers: any;
  editingForm: FormEdits;
  isLoading?: boolean;
  hideEditButton?: boolean;
  hideTitle?: boolean;
  handleStartEdit: (formID?: string) => void;
  handleCancel: () => void;
  handleSave: () => void;
  handleUpdates: (updates: any) => void;
}

const SectionView = ({
  section,
  forms,
  answers,
  editingForm,
  isLoading,
  hideEditButton = false,
  hideTitle = false,
  handleStartEdit,
  handleCancel,
  handleSave,
  handleUpdates,
}: SectionViewProps) => {
  const subSections = (section.subSections ?? []) as Array<{
    id: string;
    title: string;
  }>;
  const subSectionIds = subSections.map((s) => s.id);
  const formID = sectionFormID(section.id);
  const isEditingThis = editingForm.formID === formID;

  // memos
  const combinedFields = React.useMemo(
    () => combineSectionFields(forms, subSectionIds),
    [forms, subSectionIds],
  );

  const rows = React.useMemo(
    () => computeSectionRows(subSections, forms, answers),
    [subSections, forms, answers],
  );

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {!hideTitle && (
          <Typography
            variant='subtitle2'
            sx={{
              color: 'text.secondary',
              pb: 2,
            }}
          >
            {section.title.toUpperCase()}
          </Typography>
        )}
        {isEditingThis && answers ? (
          <Stack
            spacing={2}
            sx={{
              width: '100%',
            }}
          >
            <Editor
              fields={combinedFields}
              value={{ ...answers, ...editingForm.updates }}
              onCancel={handleCancel}
              onChange={handleUpdates}
              onSave={handleSave}
              isDisabled={false}
              isLoading={isLoading}
              isActionsDisabled
              isFloatingActions={false}
            />
            <Stack
              direction='row'
              spacing={1}
              sx={{
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <Button variant='text' data-section-action onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant='text' data-section-action onClick={handleSave}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((row) => (
              <SectionViewRow key={row.id} {...row} />
            ))}
          </Stack>
        )}
      </Box>
      {!isEditingThis && !hideEditButton && (
        <Button
          variant='text'
          data-section-action
          onClick={() => handleStartEdit(formID)}
        >
          Edit
        </Button>
      )}
    </Stack>
  );
};

const STATUS_CHIP: Record<
  FormStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  complete: { label: 'Complete', color: 'success' },
  inProgress: { label: 'In progress', color: 'warning' },
  notStarted: { label: 'Not started', color: 'default' },
};

interface FormContentProps {
  data: CategoryData;
  isLoading?: boolean;
}
const FormContent = ({ data, isLoading }: FormContentProps) => {
  const [editingForm, setEditingForm] = useState<FormEdits>({});

  const isSingleEdit =
    data.categoryID === CareProfileCategoryID.EligibilityCalculator;

  const handleCancel = useCallback(() => setEditingForm({}), [setEditingForm]);

  const handleUpdates = useCallback(
    (n: any) =>
      setEditingForm({
        ...editingForm,
        updates: { ...editingForm.updates, ...n },
      }),
    [setEditingForm, editingForm],
  );

  const handleSave = useCallback(() => {
    if (editingForm.updates !== undefined) {
      data.createManyAnswer({ ...editingForm.updates });
      data.setAnswers({ ...editingForm.updates });
    }
    setEditingForm({});
  }, [data, setEditingForm, editingForm]);

  const handleStartEdit = useCallback(
    (formID?: string) =>
      setEditingForm({
        formID: formID,
        updates: {},
      }),
    [setEditingForm],
  );

  const props = {
    answers: data.answers,
    category: data.category,
    forms: data.forms,
    editingForm: editingForm,
    isLoading: isLoading,
    handleStartEdit: handleStartEdit,
    handleCancel: handleCancel,
    handleSave: handleSave,
    handleUpdates: handleUpdates,
  };

  return (
    <Box>
      {data.sections!.map((section) => (
        <SectionView
          key={section.id}
          section={section}
          hideEditButton={isSingleEdit}
          hideTitle={isSingleEdit}
          {...props}
        />
      ))}
    </Box>
  );
};

interface FormEditAccordionProps {
  title: string;
  status: FormStatus;
  data?: CategoryData;
  isExpanded: boolean;
  onToggle: () => void;
  isLoading?: boolean;
}

export const FormEditAccordion = ({
  title,
  status,
  data,
  isExpanded,
  onToggle,
  isLoading,
}: FormEditAccordionProps) => {
  const chip = STATUS_CHIP[status];
  return (
    <Accordion expanded={isExpanded} onChange={onToggle}>
      <AccordionSummary
        id='panel-header'
        aria-controls='panel-content'
        expandIcon={<Icon icon={'chevron-down'} fontSize={16} />}
      >
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant='subtitle1'
            sx={{
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          <Chip label={chip.label} color={chip.color} size='small' />
        </Stack>
      </AccordionSummary>
      {data?.sections && (
        <AccordionDetails>
          <FormContent data={data} isLoading={isLoading} />
          {data.handlePrintPdf && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
              }}
            >
              <Button
                variant='outlined'
                size='small'
                onClick={() => data.handlePrintPdf?.()}
              >
                Download PDF
              </Button>
            </Box>
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
};

FormEditAccordion.displayName = 'FormEditAccordion';
export default FormEditAccordion;
